/**
 * Utilidades para impresión térmica de entregas
 * Genera recibos para impresoras térmicas de 58mm o 80mm
 */

/**
 * Genera el contenido del recibo de entrega para impresora térmica
 * @param {Object} entrega - Datos de la entrega
 * @returns {string} - Contenido formateado para impresora térmica
 */
export const generateThermalReceipt = (entrega) => {
  // Un comprobante de entrega solo aplica cuando ya fue entregado.
  // Mientras esté en otro estado (alistado, pendiente, en_camino) se imprime
  // un ticket de alistamiento (packing slip) con los datos y los items.
  const esEntregado = entrega.estado === 'entregado';

  if (!esEntregado) {
    return generateAlistamientoTicket(entrega);
  }

  const fecha = new Date(entrega.fechaEntrega || new Date()).toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const tipo = entrega.tipo || 'insumos';
  const items = entrega.items || [];
  const totalItems = items.reduce((sum, item) => sum + (item.cantidad || 0), 0);

  let receipt = '';

  // Header
  receipt += '================================\n';
  receipt += '           HOUSY ERP\n';
  receipt += '      Sistema de Entregas\n';
  receipt += '================================\n';
  receipt += '\n';

  // Tipo de comprobante
  receipt += '     COMPROBANTE DE ENTREGA\n';
  receipt += '\n';

  // Consecutivo
  if (entrega.consecutivo) {
    receipt += `Consecutivo: ${entrega.consecutivo}\n`;
    receipt += '\n';
  }

  // Información de la entrega
  receipt += '--------------------------------\n';
  receipt += 'DATOS DE LA ENTREGA\n';
  receipt += '--------------------------------\n';
  receipt += `Fecha: ${fecha}\n`;
  receipt += `Tipo: ${tipo === 'lenceria' ? 'Lenceria' : 'Insumos'}\n`;
  receipt += `Edificio: ${entrega.edificio?.nombre || 'N/A'}\n`;

  if (entrega.hospedaje?.nombre) {
    receipt += `Activo: ${entrega.hospedaje.nombre}\n`;
  }

  if (entrega.solicitante || entrega.creator?.nombre) {
    receipt += `Solicitante: ${entrega.solicitante || entrega.creator?.nombre}\n`;
  }

  receipt += '\n';

  // Items entregados
  receipt += '--------------------------------\n';
  receipt += `    ${tipo === 'lenceria' ? 'PRENDAS' : 'INSUMOS'} ENTREGADOS\n`;
  receipt += '--------------------------------\n';

  if (items.length > 0) {
    items.forEach((item, index) => {
      receipt += `${index + 1}. ${item.nombre}\n`;
      receipt += `   Cantidad: ${item.cantidad || 1}\n`;
      if (tipo === 'lenceria' && item.peso) {
        receipt += `   Peso: ${item.peso} g\n`;
      }
      if (item.descripcion) {
        receipt += `   Desc: ${item.descripcion}\n`;
      }
      if (item.observacion || item.observaciones) {
        receipt += `   Obs: ${item.observacion || item.observaciones}\n`;
      }
      receipt += '\n';
    });
  } else {
    receipt += 'No hay items registrados\n';
    receipt += '\n';
  }

  // Total
  receipt += '================================\n';
  receipt += `  TOTAL ITEMS: ${totalItems}\n`;
  receipt += '================================\n';
  receipt += '\n';

  // Firma
  receipt += '_________________________\n';
  receipt += '      Firma del Receptor\n';
  receipt += '\n';
  receipt += 'Confirmo haber recibido los\n';
  receipt += 'items descritos en este\n';
  receipt += 'comprobante\n';
  receipt += '\n';

  // Footer
  receipt += '--------------------------------\n';
  if (entrega.entregadoPor?.nombre) {
    receipt += `Entregado por: ${entrega.entregadoPor.nombre}\n`;
  }
  receipt += '\n';
  receipt += '   Gracias por su confianza\n';
  receipt += '================================\n';
  receipt += '\n\n\n';

  return receipt;
};

/**
 * Genera un ticket de alistamiento (packing slip) para impresora térmica.
 * Se usa mientras la entrega aún no está en estado "entregado": muestra los
 * datos del alistamiento y las prendas, pero NO incluye firma ni receptor.
 * @param {Object} entrega - Datos del alistamiento
 * @returns {string} - Contenido formateado para impresora térmica
 */
export const generateAlistamientoTicket = (entrega) => {
  const fecha = new Date(
    entrega.fecha_alistamiento || entrega.createdAt || new Date()
  ).toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const tipo = entrega.tipo || 'lenceria';
  const items = entrega.items || [];
  const totalItems = items.reduce((sum, item) => sum + (item.cantidad || 0), 0);

  let receipt = '';

  // Header
  receipt += '================================\n';
  receipt += '           HOUSY \n';
  receipt += '         ALISTAMIENTO\n';
  receipt += '================================\n';
  receipt += '\n';
  // Datos: edificio, activo (hospedaje en horizontal), fecha y hora
  receipt += '--------------------------------\n';
  receipt += `Edificio: ${entrega.edificio?.nombre || 'N/A'}\n`;
  // Activo (solo aplica a logística horizontal)
  const activoNombre = entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad;
  if (activoNombre) {
    receipt += `Activo: ${activoNombre}\n`;
  }
  receipt += `Fecha: ${fecha}\n`;
  // Observación solo si existe
  if (entrega.observaciones) {
    receipt += `Observaciones: ${entrega.observaciones}\n`;
  }
  receipt += '--------------------------------\n';
  receipt += '\n';

  // Items alistados
  receipt += '--------------------------------\n';
  receipt += ` ITEMS\n`;
  receipt += '--------------------------------\n';

  if (items.length > 0) {
    items.forEach((item, index) => {
      receipt += `${index + 1}. ${item.nombre}\n`;
      receipt += `   Cantidad: ${item.cantidad || 1}\n`;
      receipt += '\n';
    });
  } else {
    receipt += 'No hay items registrados\n';
    receipt += '\n';
  }

  // Total
  receipt += '================================\n';
  receipt += `  TOTAL ITEMS: ${totalItems}\n`;
  receipt += '================================\n';
  receipt += '\n\n\n';

  return receipt;
};

/**
 * Imprime el recibo en una impresora térmica
 * @param {Object} entrega - Datos de la entrega
 */
export const printThermalReceipt = async (entrega) => {
  try {
    const receiptContent = generateThermalReceipt(entrega);
    
    // Crear un iframe oculto para la impresión
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <style>
            @media print {
              body {
                margin: 0;
                padding: 0;
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
              }
              pre {
                margin: 0;
                padding: 0;
                white-space: pre-wrap;
                word-wrap: break-word;
              }
            }
          </style>
        </head>
        <body>
          <pre>${receiptContent}</pre>
        </body>
      </html>
    `);
    doc.close();
    
    // Esperar a que cargue y luego imprimir
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
    }, 250);
    
  } catch (error) {
    console.error('Error al imprimir recibo térmico:', error);
    throw error;
  }
};

/**
 * Genera una vista previa del recibo en una nueva ventana
 * @param {Object} entrega - Datos de la entrega
 */
export const previewThermalReceipt = (entrega) => {
  try {
    const receiptContent = generateThermalReceipt(entrega);
    const firmaUrl = entrega.firma || entrega.firmaDigital;
    const receptorNombre = entrega.receptor_nombre;
    
    const previewWindow = window.open('', '_blank', 'width=400,height=700');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Vista Previa - Recibo de Entrega</title>
          <style>
            body {
              margin: 20px;
              padding: 0;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.2;
              background-color: #f5f5f5;
            }
            .receipt-container {
              background-color: white;
              padding: 20px;
              max-width: 300px;
              margin: 0 auto;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            pre {
              margin: 0;
              padding: 0;
              white-space: pre-wrap;
              word-wrap: break-word;
            }
            .firma-section {
              margin-top: 20px;
              padding: 15px;
              border: 2px solid #4CAF50;
              border-radius: 8px;
              background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
              text-align: center;
            }
            .firma-title {
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
              font-size: 14px;
            }
            .firma-image {
              max-width: 100%;
              height: auto;
              max-height: 150px;
              margin: 10px 0;
              background: white;
              padding: 10px;
              border-radius: 4px;
              border: 1px solid #ddd;
            }
            .firma-nombre {
              margin-top: 10px;
              font-size: 11px;
              color: #666;
              font-style: italic;
            }
            .firma-error {
              color: #d32f2f;
              font-size: 11px;
              padding: 10px;
              background: #ffebee;
              border-radius: 4px;
            }
            .print-button {
              position: fixed;
              top: 10px;
              right: 10px;
              padding: 10px 20px;
              background-color: #4CAF50;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              z-index: 1000;
            }
            .print-button:hover {
              background-color: #45a049;
            }
            @media print {
              .print-button {
                display: none;
              }
              body {
                background-color: white;
                margin: 0;
              }
              .receipt-container {
                box-shadow: none;
                padding: 10px;
                max-width: 100%;
              }
              .firma-section {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <button class="print-button" onclick="window.print()">🖨️ Imprimir</button>
          <div class="receipt-container">
            <pre>${receiptContent}</pre>
            ${firmaUrl ? `
              <div class="firma-section">
                <div class="firma-title">✍️ Firma Digital del Receptor</div>
                <img 
                  src="${firmaUrl}" 
                  alt="Firma del receptor" 
                  class="firma-image"
                  onerror="this.style.display='none'; this.parentElement.innerHTML='<div class=\\'firma-error\\'>⚠️ Error al cargar la firma<br/><small>${firmaUrl}</small></div>';"
                />
                ${receptorNombre ? `<div class="firma-nombre">Firmado por: ${receptorNombre}</div>` : ''}
              </div>
            ` : ''}
          </div>
        </body>
      </html>
    `);
    previewWindow.document.close();
    
  } catch (error) {
    console.error('Error al generar vista previa:', error);
    throw error;
  }
};

/**
 * Descarga el recibo como archivo de texto
 * @param {Object} entrega - Datos de la entrega
 */
export const downloadThermalReceipt = (entrega) => {
  try {
    const receiptContent = generateThermalReceipt(entrega);
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `recibo-${entrega.consecutivo || 'entrega'}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error al descargar recibo:', error);
    throw error;
  }
};

